import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuthState } from "@/contexts/AuthContext";
import { resolveLayoutMode } from "@/lib/layoutRouting";

/**
 * Dev-only: trace AppLayout lifecycle, route changes, and auth phase transitions.
 * Confirms AppLayout mounts once and only outlet content swaps.
 */
export function useLayoutMountDebug(componentName = "AppLayout") {
    const { pathname } = useLocation();
    const { authPhase } = useAuthState();
    const mountedRef = useRef(false);
    const lastRouteRef = useRef(pathname);
    const lastPhaseRef = useRef(authPhase);

    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return undefined;

        if (!mountedRef.current) {
            mountedRef.current = true;
            console.debug(`[layout:${componentName}] MOUNT (expect once per session)`);
        }

        return () => {
            if (process.env.NODE_ENV === "development") {
                console.warn(`[layout:${componentName}] UNMOUNT — layout shell should not remount during navigation`);
            }
        };
    }, [componentName]);

    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return;
        if (lastRouteRef.current === pathname) return;
        console.debug(
            `[layout:${componentName}] route ${lastRouteRef.current} → ${pathname} (mode=${resolveLayoutMode(pathname)})`,
        );
        lastRouteRef.current = pathname;
    }, [pathname, componentName]);

    useEffect(() => {
        if (process.env.NODE_ENV !== "development") return;
        if (lastPhaseRef.current === authPhase) return;
        console.debug(`[layout:${componentName}] authPhase ${lastPhaseRef.current} → ${authPhase}`);
        lastPhaseRef.current = authPhase;
    }, [authPhase, componentName]);
}
